import { SUPPORTED_COUNTRY_CODES } from "./constants";

// UK + EEA (EU + Iceland, Liechtenstein, Norway) ISO 3166-1 alpha-2 codes —
// the markets where PostHog capture must be consent-gated under UK GDPR/PECR
// and EU GDPR. Other supported markets (NG, US, GH, KE, BJ, AU, CA) are not
// gated.
const EEA_UK_COUNTRY_CODES = new Set([
  "GB",
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO",
]);

const DEBUG_COUNTRY_STORAGE_KEY = "ngw_debug_country";
const DEBUG_COUNTRY_QUERY_PARAM = "debug_country";

// Lets engineers exercise the consent banner in local dev (react-scripts
// start never gets the server-injected window.__NGW_COUNTRY__ value, since
// only server.js sets it) via ?debug_country=GB, persisted across reloads.
function getDebugCountryOverride() {
  if (typeof window === "undefined") return null;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get(
      DEBUG_COUNTRY_QUERY_PARAM,
    );
    if (fromQuery) {
      const normalized = fromQuery.toUpperCase();
      localStorage.setItem(DEBUG_COUNTRY_STORAGE_KEY, normalized);
      return normalized;
    }
    return localStorage.getItem(DEBUG_COUNTRY_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getVisitorCountry() {
  const debugOverride = getDebugCountryOverride();
  if (debugOverride) return debugOverride;
  if (typeof window === "undefined") return null;
  return window.__NGW_COUNTRY__ || null;
}

// Country for pre-selecting a phone country picker: the visitor's IP country
// (Cloudflare's cf-ipcountry, injected by server.js) when it's one we support,
// otherwise `fallback`. Unknown/unsupported values ("XX", "T1" for Tor, a
// country we don't operate in) all fall through to the fallback.
export function getSupportedVisitorCountry(fallback = "NG") {
  const country = getVisitorCountry()?.toUpperCase();
  return SUPPORTED_COUNTRY_CODES.includes(country) ? country : fallback;
}

// Safety-first default: an undetermined country (header missing, local dev,
// infra gap) is treated as consent-required rather than as an ungated
// market — this over-shows the banner rather than risking silently skipping
// consent for a UK/EEA visitor.
export function isConsentRequiredMarket() {
  const country = getVisitorCountry();
  if (!country) return true;
  return EEA_UK_COUNTRY_CODES.has(country.toUpperCase());
}
